import type { CanvasMetadata, SchemaModel, TableModel } from '@bits2schema/shared';
import type { Command as CanvasCommand } from '../../canvas/commands';
import { createClearTableCanvasStateCommand, createDeleteNoteCommand, createRemoveTableFromGroupCommand } from '../../canvas/commands';
import { useCommandStack } from '../../command/stack';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useT } from '../../i18n';
import type { SchemaCommand } from '../commands';
import { createDeleteTableCommand } from '../commands';
import { findTableDependents } from '../mutations';

interface DeleteTableDialogProps {
  table: TableModel;
  schema: SchemaModel;
  canvas: CanvasMetadata;
  schemaExecute: (command: SchemaCommand) => void;
  canvasExecute: (command: CanvasCommand) => void;
  onClose: () => void;
}

/**
 * Potvrzení smazání tabulky (E4-US3) — blokuje, pokud na tabulku odkazuje FK
 * z jiné tabulky (AC), jinak jen varuje před vedlejšími efekty na canvasu
 * (skupina, poznámky) a po potvrzení zavolá schema i canvas commandy
 * sekvenčně. Epic 13 `runGroup` je spojí do jednoho vratného kroku — undo
 * obnoví tabulku i její canvas metadata (poznámky, členství ve skupině)
 * najednou, ne po dílčích krocích (E13-US4 AC).
 */
export function DeleteTableDialog({ table, schema, canvas, schemaExecute, canvasExecute, onClose }: DeleteTableDialogProps) {
  const t = useT();
  const { runGroup } = useCommandStack();
  const dependents = findTableDependents(schema, table.id);
  const blocked = dependents.length > 0;
  const group = canvas.groups.find((candidate) => candidate.memberTableIds.includes(table.id));
  const attachedNotes = canvas.notes.filter((note) => {
    const attachment = note.attachment;
    if (attachment.type === 'table') return attachment.id === table.id;
    if (attachment.type === 'column') return table.columns.some((column) => column.id === attachment.id);
    return false;
  });

  const confirm = () => {
    runGroup({ key: 'schema.command.deleteTable', params: { name: table.name } }, () => {
      const originalIndex = schema.tables.findIndex((candidate) => candidate.id === table.id);
      schemaExecute(createDeleteTableCommand(table, originalIndex));

      for (const note of attachedNotes) {
        canvasExecute(createDeleteNoteCommand(note, canvas.notes.findIndex((candidate) => candidate.id === note.id)));
      }
      if (group) {
        canvasExecute(createRemoveTableFromGroupCommand(group.id, table.id, group.memberTableIds.indexOf(table.id)));
      }
      canvasExecute(
        createClearTableCanvasStateCommand(table.id, canvas.tablePositions[table.id], canvas.tableColors[table.id] ?? null),
      );
    });
    onClose();
  };

  return (
    <ConfirmDialog
      title={t('inspector.table.deleteConfirmTitle', { name: table.name })}
      confirmLabel={t('common.delete')}
      danger
      confirmDisabled={blocked}
      onCancel={onClose}
      onConfirm={confirm}
      message={
        <>
          {blocked ? (
            <p className="notice notice--error">
              {t('inspector.table.deleteConfirmBlocked', {
                list: dependents.map((dependent) => `${dependent.table.name}.${dependent.foreignKey.name}`).join(', '),
              })}
            </p>
          ) : (
            <>
              <p>{t('inspector.table.deleteConfirmQuestion')}</p>
              {group && <p className="hint">{t('inspector.table.deleteConfirmWarningGroup', { name: group.name })}</p>}
              {attachedNotes.length > 0 && (
                <p className="hint">{t('inspector.table.deleteConfirmWarningNotes', { count: attachedNotes.length })}</p>
              )}
            </>
          )}
        </>
      }
    />
  );
}
